import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
	initialize,
	destroy,
	reduxForm,
	getFormSyncErrors,
	getFormValues,
	unregisterField
} from "redux-form";

import { loadConcept } from "reducers/ConceptDetails";
import useActiveCreative from "hooks/useActiveCreative";
import { closeModal } from "reducers/Modal";
import { Blade, Button, ProgressIndicator } from "rhombus-react";
import { MODAL_TYPES } from "utils/constants";
import { adSizes } from "./CreateDisplayConceptModal";
import { buildInitialConceptSizes } from "./AddAdStudioCreativeToConcept";
import SelectAdsizesForConcept from "./SelectAdsizesForConcept";
import PartnerCreativePane from "./PartnerCreativePane";
import { ADD_CREATIVE_TAGS_TO_CONCEPT_FORM } from "widgets/forms/list";
import { loadCreatives } from "reducers/Creatives";
import { buildDisplayTagSizes, customSizeValidation } from "./SelectCreativeTypePane";

import "./AddCreativeTagsToConcept.css";

function AddCreativeTagsToConcept(props) {
	const { handleSubmit, pristine, valid } = props;
	const dispatch = useDispatch();
	const {
		isOpen,
		modalType,
		modalOptions,
		conceptSizes,
		isLoaded,
		isSaving,
		formErrors,
		formValues,
	} = useSelector((state) => {
		const {
			isLoaded,
			isSaving,
			data: { sizes: conceptSizes },
		} = state.conceptDetails || {};

		return {
			isOpen: state.modal.isOpen,
			modalType: state.modal.modalType,
			modalOptions: state.modal.modalOptions,
			formErrors: {
				errors: getFormSyncErrors(ADD_CREATIVE_TAGS_TO_CONCEPT_FORM)(state) || {},
			},
			formValues: getFormValues(ADD_CREATIVE_TAGS_TO_CONCEPT_FORM)(state),
			conceptSizes,
			isSaving,
			isLoaded,
		};
	});

	const [step, setStep] = useState(0);
	const [apiErrors, setApiErrors] = useState(false);
	const { libraryId, creativeId, conceptId, conceptName } = modalOptions;

	useActiveCreative(creativeId);

	const isModalOpen = useMemo(() => {
		return isOpen && modalType === MODAL_TYPES.ADD_CREATIVE_TAGS_TO_CONCEPT;
	}, [isOpen, modalType]);

	useEffect(() => {
		dispatch(destroy(ADD_CREATIVE_TAGS_TO_CONCEPT_FORM));
		return () => {
			dispatch(destroy(ADD_CREATIVE_TAGS_TO_CONCEPT_FORM));
		};
	}, []);

	useEffect(() => {
		if(!libraryId || !conceptId || !isModalOpen){ return; }
		dispatch(loadConcept(libraryId, conceptId));
	}, [libraryId, conceptId, isModalOpen]);

	let initialValues = useMemo(() => (
		{ conceptName, ...buildInitialConceptSizes(conceptSizes, adSizes) }
	), [conceptSizes]);

	useEffect(() => {
		if(!initialValues || !isModalOpen){ return; }
		dispatch(initialize(ADD_CREATIVE_TAGS_TO_CONCEPT_FORM, { ...initialValues, "display-tags": {} }));
	}, [initialValues]);

	let { hasNewSizes, newTagSizes } = useMemo(() => {
		const {
				"base-sizes": baseSizes = {},
				"custom-sizes": customSizes  = [],
			} = formValues || {}
			, newTagSizes = buildDisplayTagSizes(baseSizes, customSizes, initialValues)
			, customSizesInvalid = customSizes.some(customSizeValidation);

		return {
			newTagSizes,
			hasNewSizes: newTagSizes.length > 0 && !customSizesInvalid,
		};
	}, [formValues, initialValues]);

	function stepBack() {
		newTagSizes.forEach((size) => {
			dispatch(unregisterField(ADD_CREATIVE_TAGS_TO_CONCEPT_FORM, `display-tags.${size}`));
		});
		setStep(step-1);
	}

	function stepForward() {
		setStep(step+1);
	}

	function renderSteps(){
		if(step === 0) {
			return (
				<SelectAdsizesForConcept
					formName={ADD_CREATIVE_TAGS_TO_CONCEPT_FORM}
					handleSubmit={handleSubmit}
					pristine={pristine}
					libraryId={libraryId}
					conceptId={conceptId}
					initialValues={initialValues}
					formErrors={formErrors}
				 />
			);
		}
		if(step === 1) {
			return (
				<PartnerCreativePane
					libraryId={libraryId}
					formErrors={formErrors}
					setApiErrors={setApiErrors}
					formName={ADD_CREATIVE_TAGS_TO_CONCEPT_FORM}
					handleSubmit={handleSubmit}
					valid={valid}
					initialValues={initialValues}
					isNewConcept={false}
					conceptId={conceptId}
					handleSubmitSuccess={() => {
						dispatch(loadCreatives(libraryId));
						dispatch(closeModal());
					}}
				/>
			);
		}
	}

	function renderContent() {
		return (
			<div className="progress-container">
				<ProgressIndicator isLoading={!isLoaded || isSaving} />
				{ renderSteps() }
			</div>
		);
	}

	function renderFooter(){
		if(step === 0) {
			return (
				<>
					<Button theme="default" disabled={!isLoaded} onClick={onClose}>Cancel</Button>
					<Button
						key="step forward"
						theme="blue"
						type="button"
						onClick={stepForward}
						disabled={!isLoaded || pristine || !hasNewSizes }
					>Next To Add Creative Tags</Button>
				</>
			);
		}
		if(step === 1) {
			return (
				<>
					<Button theme="default" disabled={!isLoaded || isSaving} onClick={stepBack}>Back</Button>
					<Button
						key="submit"
						theme="green"
						type="submit"
						disabled={!isLoaded || isSaving || !valid || apiErrors}
						form={ADD_CREATIVE_TAGS_TO_CONCEPT_FORM}
					>Add Creative Tags To Concept</Button>
				</>
			);
		}
	}

	function onClose() {
		dispatch(closeModal());
	}

	let modalProps = {
		title: `Add New Creative Tag to - ${conceptName}`,
		setAppTitle: false,
		type: "modal",
		className: "add-tags-to-concept",

		xclose: true,
		allowFullScreen: false,
		bladeProps: {
			verticallyCenter: true,
		},

		renderContent,
		renderFooter,
		onClose,
	};

	if(!isModalOpen){ return null; }
	return <Blade {...modalProps} />;
}

export default reduxForm({
	form: ADD_CREATIVE_TAGS_TO_CONCEPT_FORM,
	shouldValidate: () => true,
	initialValues: {
		conceptName: "",
		"base-sizes": {},
		"custom-sizes": [],
		"display-tags": {},
	},
	destroyOnUnmount: false,
})(AddCreativeTagsToConcept);
